/**
 * Moving or granting one person's access between partners.
 *
 * The people list is read per source partner rather than network-wide: the
 * operation is always "take this person from THAT partner", and a network-wide
 * people search would need an endpoint that does not exist and would offer
 * thousands of names with no useful ordering.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useOrg } from '@/admin/hooks/use-org'
import api from '@/admin/lib/api'

export interface PartnerPerson {
  uuid: string
  user_uuid: string
  user_email: string
  user_name: string
}

export interface AccessChange {
  explicit_access_granted: boolean
  explicit_access_revoked: boolean
  membership_created: boolean
  membership_revoked: boolean
  role_name: string
  inherited_access: boolean
  /** The move un-suspended the target partner (migrated-dealer onboarding). */
  target_reactivated: boolean
  /** The partner they came from, so the move can be reversed. */
  source_partnership_uuid: string
}

export function usePartnerPeople(partnershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  return useQuery<{ results: PartnerPerson[] }>({
    queryKey: ['partnership-people', orgUuid, partnershipUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnerships/${partnershipUuid}/user-access/`)
        .then((r) => r.data),
    enabled: !!orgUuid && !!partnershipUuid,
  })
}

export function useMoveAccess(targetPartnershipUuid: string | undefined) {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<
    AccessChange,
    unknown,
    { userUuid: string; sourcePartnershipUuid: string | null }
  >({
    mutationFn: ({ userUuid, sourcePartnershipUuid }) =>
      api
        .post(
          `/organisations/${orgUuid}/partnerships/${targetPartnershipUuid}/access-move/`,
          {
            user_uuid: userUuid,
            source_partnership_uuid: sourcePartnershipUuid,
          },
        )
        .then((r) => r.data),
    onSuccess: () => {
      // Both partners' people lists change, and the detail page reads one of
      // them - without this the person appears to still be where they were.
      queryClient.invalidateQueries({ queryKey: ['partnership-people'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-detail'] })
    },
  })
}
